import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  CMS_ADMIN_NAV,
  CMS_PRODUCT_NAV_STATIC,
  CMS_PRODUCT_NAV_TAIL,
  buildAccessoryNavChildren,
  buildComponentNavChildren,
  type CmsNavChild,
  type CmsNavNode,
} from '../cmsNavModel';
import type { CmsCatalogRow } from 'app/services/cmsApi';
import { CMS_SCREENS_BY_KEY, cmsRouteKey } from '../cmsScreenRegistry';

/**
 * The CMS Admin left nav — `view/main.menu.js` + `view/product.menu.js`
 * rendered into ONE `<nav>`, which is what legacy actually does:
 * `product.menu.js:render()` does `$("#main_menu nav").append(...)`, so the
 * product section lives inside the SAME `<nav>` as the administrative section
 * and `#product_menues` stays empty (measured: `productMenuesHtmlLen: 0`).
 *
 * Behaviours reproduced from `main.menu.js`:
 *  - `openCmsTabs` (:50-68): clicking an anchor marks it `selected`, clears
 *    other `selected` anchors, and toggles the `active` class between
 *    `#menuTab` and `#productsTabs` based on `data-type="product"`.
 *  - `setSelected` (:69-81): the anchor's parent collapse button also gets
 *    `selected` (that is what keeps "Costing" highlighted while "Freight" is
 *    the open screen).
 *  - `scrollToColappse` (:82-107): opening a group CLOSES any other open group
 *    — Bootstrap's `data-toggle="collapse"` allows several open at once, but
 *    this handler explicitly triggers a click on the currently-expanded button
 *    first. Reproduced as single-open state.
 *
 * The `+`/`−` affordance is legacy's `i.icon-add` glyph; the stylesheet swaps it
 * on `aria-expanded`. The Modular Frame group is the one group with no such
 * icon (`product.menu.tmpl:105-110`).
 */

interface CmsNavProps {
  /** Currently open screen key — drives `selected`. */
  activeKey: string;
  onOpen: (key: string) => void;
  componentCatalog: CmsCatalogRow[];
  accessoryCatalog: CmsCatalogRow[];
}

const CmsNav = ({ activeKey, onOpen, componentCatalog, accessoryCatalog }: CmsNavProps) => {
  // Legacy allows exactly one expanded group (main.menu.js:88).
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const toggleGroup = useCallback((id: string) => {
    setOpenGroup((cur) => (cur === id ? null : id));
    /**
     * `main.menu.js:scrollToColappse` (:91-107) — the SECOND half of the
     * handler, and it is not decorative: `nav` is `overflow:auto` with
     * `max-height:calc(100vh - 110px)`, so a long group (Accessories has 19
     * rows) opens partly below the fold. 400 ms after the click legacy looks at
     * the element AFTER the panel (`#main_menu <target> + *`) and, if it is not
     * fully in the viewport, `scrollIntoView({behavior:'smooth', block:'end'})`.
     * Measured on the live page: opening Accessories leaves `nav.scrollTop` at
     * 160, not 0. Runs on close as well — there the sibling is normally already
     * visible, so it is a no-op, exactly as in legacy.
     *
     * The `+ *` sibling walk is why `renderGroup` must not wrap the
     * button/`ul` pair in an element (see the note there).
     */
    window.setTimeout(() => {
      const el = document.querySelector(`#main_menu #${id} + *`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const fullyVisible =
        r.top >= 0 &&
        r.left >= 0 &&
        r.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        r.right <= (window.innerWidth || document.documentElement.clientWidth);
      if (!fullyVisible) el.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 400);
  }, []);

  // `available_components` — `product.menu.js:render()` seeds it with the
  // literal `[100]` then fills it from the same collection it renders.
  const availableComponentIds = [
    100,
    ...componentCatalog.map((r) => r.component_id).filter((v): v is number => v !== undefined),
  ];

  const componentChildren = buildComponentNavChildren(componentCatalog, availableComponentIds);
  const accessoryChildren = buildAccessoryNavChildren(accessoryCatalog);

  /** Which collapse group (if any) contains the active screen. */
  const groupOfActive = (() => {
    const inGroup = (children: readonly { key: string }[]) =>
      children.some((c) => c.key === activeKey);
    for (const node of CMS_ADMIN_NAV) {
      if (node.kind === 'group' && inGroup(node.children)) return node.id;
    }
    if (inGroup(CMS_PRODUCT_NAV_STATIC.settings.children)) return CMS_PRODUCT_NAV_STATIC.settings.id;
    if (inGroup(CMS_PRODUCT_NAV_STATIC.construction.children))
      return CMS_PRODUCT_NAV_STATIC.construction.id;
    if (inGroup(CMS_PRODUCT_NAV_STATIC.modular_frame.children))
      return CMS_PRODUCT_NAV_STATIC.modular_frame.id;
    if (inGroup(componentChildren)) return 'components';
    if (inGroup(accessoryChildren)) return 'accessories';
    return null;
  })();

  /**
   * ★ A HASH-OPENED SCREEN MUST REVEAL ITS NAV GROUP.
   *
   * Legacy gets this free from `router.js:clickToElement` (:47-55), which before
   * clicking the anchor also clicks
   * `#main_menu nav button[data-target="#<groupId>"]` — the collapse toggle of
   * the group the anchor lives in. `useCmsHashRoute` deliberately does not model
   * `clickToElement`, and until T1 that was invisible: every screen shipped by
   * the shell run (`manufacturer`, `network`, `document`) is a TOP-LEVEL nav
   * anchor, so the accordion path was never exercised. `freight` is the first
   * live screen inside a collapse group and it exposed the gap.
   *
   * MEASURED 2026-08-05, deep link `/admin/#freight`, cold load:
   *     :8130  #costing = "collapse in", Freight anchor visible, `selected`
   *     :8005  #costing = "collapse",    Freight anchor height 0 (HIDDEN)
   * i.e. a bookmarked link landed on the right screen with the left nav showing
   * no indication of where the user was.
   *
   * ★ THIS ONLY EVER EXPANDS — it never closes a group, because legacy does not:
   *  · clicking a TOP-LEVEL item (e.g. Documents) leaves an already-open group
   *    open (measured on `:8130`), so `groupOfActive === null` must be a no-op;
   *  · a group the user collapsed BY HAND stays collapsed, because the effect is
   *    keyed on `activeKey`/`groupOfActive` and neither changes when the user
   *    works the toggle.
   * `groupOfActive` is in the deps for the product groups, whose children arrive
   * with the catalog fetch — on a component/accessory deep link it resolves from
   * `null` to the group id after the screen key is already set.
   *
   * ⚠ RESIDUAL, PARKED (not reproduced on purpose): because legacy TOGGLES that
   * button rather than opening it, navigating FORWARD to a screen whose group is
   * already open COLLAPSES it on `:8130`. That is legacy toggle-bug behaviour;
   * reproducing it would need the whole synthetic-click machinery the shell
   * deliberately dropped. See the T1 verification report.
   */
  useEffect(() => {
    if (groupOfActive) setOpenGroup(groupOfActive);
  }, [activeKey, groupOfActive]);

  const renderChild = (child: CmsNavChild, extraClass?: string) => (
    <li
      key={child.key}
      className={child.disabled ? 'non-clickable' : undefined}
      data-component-id={child.dataComponentId}
      data-accessory-id={child.dataAccessoryId}
    >
      <a
        value={child.key}
        data-type={child.dataType}
        className={[child.itemClass ?? extraClass, activeKey === child.key ? 'selected' : '']
          .filter(Boolean)
          .join(' ')}
        /* Only `permissions_ahu` carries one, and it is what makes that row read
           "AHU" rather than the nav's `text-transform:capitalize` "Ahu". */
        style={child.inlineStyle}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpen(child.key);
        }}
      >
        {/* ★ Legacy's own markup is inconsistent and BOTH shapes were measured on
            the live nav: the 7 `permissions_*` rows and the 2 Numbering rows wrap
            the label in an extra `<div class="">` (`main.menu.tmpl:41-100`), while
            all 48 other sub-items put the label straight into `.col-xs-12`
            (`<div class="col-xs-12">Costs</div>`). Normalising either way leaves a
            structural diff, so the flag carries it per row. */}
        <div className="col-xs-12">
          {child.innerLabelDiv ? <div className="">{child.label}</div> : child.label}
        </div>
      </a>
    </li>
  );

  const renderGroup = (
    id: string,
    label: string,
    icon: string,
    showToggleIcon: boolean,
    children: CmsNavChild[],
    productChild?: boolean
  ) => {
    const expanded = openGroup === id;
    // ★ NO WRAPPER ELEMENT. `styles.css` sizes the nav rows through selectors
    // that assume `button` and `ul` are DIRECT children of `nav` (and adjacent
    // siblings — `main.menu.js:scrollToColappse` even queries
    // `#main_menu ${target} + *`). A wrapper `<div>` here silently shortened
    // every collapse row by ~6px and broke the `+ *` sibling walk: the
    // recurring "legacy-CSS-selector miss" failure class.
    return (
      <Fragment key={id}>
        <button
          type="button"
          className={[
            'menu-collapse',
            productChild ? 'product-child' : '',
            groupOfActive === id ? 'selected' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          data-target={`#${id}`}
          aria-expanded={expanded}
          onClick={() => toggleGroup(id)}
        >
          <div className="col-xs-12">
            <i className={icon} />
            {label}
            {showToggleIcon && <i className="icon-add" />}
          </div>
        </button>
        <ul
          id={id}
          className={['collapse', expanded ? 'in' : '', productChild ? 'product-child' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {children.map((c) => renderChild(c))}
        </ul>
      </Fragment>
    );
  };

  const renderNode = (node: CmsNavNode) => {
    if (node.kind === 'heading') {
      /**
       * ★ T2: a heading CAN be `selected`. `skeleton.css:602` gives `.nav-heading`
       * `pointer-events: none`, so a user cannot click one — but `router.js`
       * opens a screen by SYNTHESISING a jQuery `.click()`, which ignores
       * `pointer-events` and reaches `main.menu.js:setSelected`. Measured on
       * `:8130` with a cold `/admin/#products`: the Products heading renders
       * `class="nav-heading selected"` while its screen is open.
       *
       * Matched on the HASH FRAGMENT, because this heading's `value` is
       * `products` while the screen it opens is the slot `all_products`.
       * Correctly NO `onClick` — legacy's real mouse click is dead here
       * (measured: 0 requests, hash untouched).
       */
      const activeScreen = CMS_SCREENS_BY_KEY[activeKey];
      const headingActive = !!activeScreen && cmsRouteKey(activeScreen) === node.key;
      return (
        <a
          key={node.label}
          value={node.key}
          className={['nav-heading', node.extraClass, headingActive ? 'selected' : '']
            .filter(Boolean)
            .join(' ')}
        >
          <div className="col-xs-12">
            <i className={node.icon} />
            {node.label}
          </div>
        </a>
      );
    }
    if (node.kind === 'item') {
      return (
        <a
          key={node.key}
          value={node.key}
          data-type={node.dataType}
          id={node.key === 'manufacturer' ? 'default-tab' : undefined}
          className={[
            node.itemClass,
            node.dataType === 'product' ? 'product-child' : '',
            activeKey === node.key ? 'selected' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpen(node.key);
          }}
        >
          <div className="col-xs-12">
            <i className={node.icon} />
            {node.label}
          </div>
        </a>
      );
    }
    return renderGroup(node.id, node.label, node.icon, node.showToggleIcon, node.children);
  };

  const PRODUCT_GROUP_ICON = 'icon-folder-open-24px';

  return (
    <>
      <div className="col-xs-12 menu-header-part">
        <input id="left_resizer" type="checkbox" className="left-menu-resizer whiteboard-resizer" />
        <label className="toggle-menu-header">
          <div className="menu-header-text">Content Management System</div>
        </label>
      </div>
      <nav className="navbar navbar-inverse navbar-static-top" role="navigation">
        {CMS_ADMIN_NAV.map(renderNode)}

        {/* ── product section: appended into the SAME <nav> by product.menu.js ── */}
        {renderGroup(
          CMS_PRODUCT_NAV_STATIC.settings.id,
          CMS_PRODUCT_NAV_STATIC.settings.label,
          PRODUCT_GROUP_ICON,
          CMS_PRODUCT_NAV_STATIC.settings.showToggleIcon,
          CMS_PRODUCT_NAV_STATIC.settings.children.map((c) => ({
            ...c,
            dataType: 'product' as const,
            itemClass: CMS_PRODUCT_NAV_STATIC.settings.itemClass,
          })),
          true
        )}
        {renderGroup(
          CMS_PRODUCT_NAV_STATIC.construction.id,
          CMS_PRODUCT_NAV_STATIC.construction.label,
          PRODUCT_GROUP_ICON,
          CMS_PRODUCT_NAV_STATIC.construction.showToggleIcon,
          CMS_PRODUCT_NAV_STATIC.construction.children.map((c) => ({
            ...c,
            dataType: 'product' as const,
            itemClass: CMS_PRODUCT_NAV_STATIC.construction.itemClass,
          })),
          true
        )}
        {renderGroup(
          CMS_PRODUCT_NAV_STATIC.modular_frame.id,
          CMS_PRODUCT_NAV_STATIC.modular_frame.label,
          PRODUCT_GROUP_ICON,
          CMS_PRODUCT_NAV_STATIC.modular_frame.showToggleIcon,
          CMS_PRODUCT_NAV_STATIC.modular_frame.children.map((c) => ({
            ...c,
            dataType: 'product' as const,
            itemClass: CMS_PRODUCT_NAV_STATIC.modular_frame.itemClass,
          })),
          true
        )}
        {renderGroup('components', 'components', PRODUCT_GROUP_ICON, true, componentChildren, true)}
        {renderGroup('accessories', 'accessories', PRODUCT_GROUP_ICON, true, accessoryChildren, true)}

        {CMS_PRODUCT_NAV_TAIL.map(renderNode)}
      </nav>
    </>
  );
};

export default CmsNav;
